import { useState } from 'react';
import type { Invoice } from '@shared/types';
import { useAuth } from '../app/auth';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { formatDateTime, formatMoney } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Modal,
  Notice,
  PageHeader,
  Pagination,
  Select,
  Table,
  Textarea,
  type Column,
} from '../components/ui';

interface InvoiceList {
  items: Invoice[];
  total: number;
  page: number;
  pageSize: number;
}

const STATUS_TONE = {
  requested: 'warn',
  issued: 'ok',
  rejected: 'danger',
} as const;

const STATUS_LABEL = {
  requested: '待开具',
  issued: '已开具',
  rejected: '已驳回',
} as const;

/** 发票：财务确认后手动开具（不接电子发票 API）。 */
export function InvoicesPage() {
  const { can } = useAuth();
  const canSettle = can('order.refund');

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('requested');
  const [version, setVersion] = useState(0);
  const [selected, setSelected] = useState<Invoice | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invoices = useAsync<InvoiceList>(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: '20' });
    if (status !== '') params.set('status', status);
    return api.get(`/api/invoices?${params.toString()}`);
  }, [page, status, version]);

  async function settle(next: 'issued' | 'rejected') {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/invoices/${selected.id}/settle`, {
        status: next,
        reason: next === 'rejected' ? reason.trim() || '资料不完整' : null,
      });
      setSelected(null);
      setReason('');
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<Invoice>[] = [
    {
      key: 'title',
      header: '发票',
      render: (row) => (
        <div>
          <p className="text-fg">{row.title}</p>
          <p className="text-xs text-fg-subtle">
            {row.email}
            {row.taxNo ? ` · 税号 ${row.taxNo}` : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'amount',
      header: '金额',
      render: (row) => <span className="tabular">{formatMoney(row.amountMinor, row.currency)}</span>,
    },
    {
      key: 'period',
      header: '时间段',
      render: (row) => (
        <span className="text-xs text-fg-muted">
          {row.periodFrom} ~ {row.periodTo}
        </span>
      ),
    },
    {
      key: 'status',
      header: '状态',
      render: (row) => <Badge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Badge>,
    },
    {
      key: 'created',
      header: '申请时间',
      render: (row) => <span className="text-xs text-fg-muted">{formatDateTime(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '90px',
      render: (row) =>
        canSettle && row.status === 'requested' ? (
          <Button size="sm" onClick={() => setSelected(row)}>
            处理
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="发票"
        description="客户按时间段申请；金额由支付侧按已付且未退回的订单重算，后台只需确认开具或驳回。"
      />

      {error && selected === null ? <Notice tone="danger">{error}</Notice> : null}

      <Card>
        <CardHeader title="开票申请" hint={`${invoices.data?.total ?? 0} 条`} />
        <div className="mb-3 w-40">
          <Select
            value={status}
            onChange={(value) => {
              setStatus(value);
              setPage(1);
            }}
            options={[
              { value: 'requested', label: '待开具' },
              { value: '', label: '全部状态' },
              { value: 'issued', label: '已开具' },
              { value: 'rejected', label: '已驳回' },
            ]}
          />
        </div>
        <Table
          columns={columns}
          rows={invoices.data?.items ?? []}
          getKey={(row) => row.id}
          loading={invoices.loading}
          empty="没有符合条件的发票申请"
        />
        <Pagination
          page={page}
          pageSize={invoices.data?.pageSize ?? 20}
          total={invoices.data?.total ?? 0}
          onChange={setPage}
        />
      </Card>

      <Modal
        open={selected !== null}
        title="处理开票申请"
        onClose={() => setSelected(null)}
        footer={
          <>
            <Button onClick={() => setSelected(null)}>取消</Button>
            <Button variant="primary" loading={busy} onClick={() => void settle('issued')}>
              确认已开具
            </Button>
          </>
        }
      >
        {selected ? (
          <div className="space-y-3">
            {error ? <Notice tone="danger">{error}</Notice> : null}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              <div>
                <dt className="text-fg-muted">抬头</dt>
                <dd className="text-fg">{selected.title}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">税号</dt>
                <dd className="text-fg">{selected.taxNo ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">金额</dt>
                <dd className="tabular text-fg">{formatMoney(selected.amountMinor)}</dd>
              </div>
              <div>
                <dt className="text-fg-muted">邮箱</dt>
                <dd className="text-fg">{selected.email}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-fg-muted">时间段</dt>
                <dd className="text-fg">
                  {selected.periodFrom} ~ {selected.periodTo}
                </dd>
              </div>
            </dl>

            <Field label="驳回原因" hint="仅在驳回时填写，会展示给客户">
              <Textarea value={reason} onChange={setReason} rows={2} maxLength={200} />
            </Field>
            <Button variant="danger" loading={busy} onClick={() => void settle('rejected')}>
              驳回申请
            </Button>
            <Notice tone="info">
              后台不生成发票文件：请在你的开票系统里开好后，再点「确认已开具」。
            </Notice>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
